import {
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { ShareLinkObject } from '@client/graphql/__generated__/types';
import { PhoneNumberInputGroup } from '~/common/components/Inputs/PhoneNumberInputGroup';
import { UserAccessRow } from './UserAccessRow';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  email: string;
  setEmail: (email: string) => void;
  message: string;
  setMessage: (message: string) => void;
  shareLinks: ShareLinkObject[];
  revokeShareLink: (token: string) => void;
  loading: boolean;
  onShare: () => Promise<void>;
  onPreview?: () => void;
}

export const ShareModal = ({
  isOpen,
  onClose,
  title,
  phoneNumber,
  setPhoneNumber,
  email,
  setEmail,
  message,
  setMessage,
  shareLinks,
  revokeShareLink,
  loading,
  onShare,
  onPreview,
}: ShareModalProps) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bgColor="bg.main">
        <ModalCloseButton />
        <ModalHeader>
          <Flex gap={1}>
            <Text color="whiteAlpha.600">Share</Text>
            <Text>{title}</Text>
          </Flex>
        </ModalHeader>
        <ModalBody>
          <Flex direction="column" gap={4}>
            <FormControl>
              <FormLabel>Share by phone number</FormLabel>
              <PhoneNumberInputGroup
                data-testid="share-by-phone-number-input"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>And/or Share by email</FormLabel>
              <Input
                data-testid="share-by-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Message</FormLabel>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </FormControl>
            {!!shareLinks && shareLinks.length > 0 && (
              <>
                <Divider />
                <FormControl>
                  <FormLabel>Who has access</FormLabel>
                  <Flex direction="column" gap={1}>
                    {shareLinks?.map((link) => (
                      <UserAccessRow
                        key={link.id}
                        email={link.recipientEmail}
                        phoneNumber={link.recipientPhoneNumber}
                        onRemove={() => void revokeShareLink(link.token)}
                      />
                    ))}
                  </Flex>
                </FormControl>
              </>
            )}
          </Flex>
        </ModalBody>
        <Divider />
        <ModalFooter justifyContent="space-between">
          <Text color="whiteAlpha.600" fontSize="sm" mr={1}>
            They will receive a view-only copy
          </Text>
          <Flex gap={2}>
            {onPreview && (
              <Button size="xs" variant="link" onClick={() => void onPreview()}>
                Show Preview
              </Button>
            )}
            <Tooltip
              hasArrow
              isDisabled={!!email || !!phoneNumber}
              label="Please provide an email or phone number to share"
            >
              <Button
                isDisabled={!email && !phoneNumber}
                isLoading={loading}
                size="xs"
                onClick={() => void onShare()}
              >
                Share
              </Button>
            </Tooltip>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
